import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { VisualizationTextMarkupRenderer } from '@mentimeter/tiptap/visualization-text-markup-renderer';
import type { ScaleBand } from 'd3-scale';
import { useRef } from 'react';
import { TextFitGhost } from '../components/text-fit-ghost/TextFitGhost';
import type { RenderChoiceLabel } from '../utils/types/render-options-by-slide-type/types';
import { VISUALIZATION_LABEL_FONTSIZES } from '../utils/constants';
import { getLongestLabel } from '../utils/get-longest-label';
import { type ScalesData, type ScalesOptions } from './Scales';
import {
  MARGIN_HORIZONTAL,
  SKIP_TEXT_WIDTH,
  getAverageScoreCircleRadius,
} from './constants';

interface ScalesOptionLabelsProps {
  width: number;
  height: number;
  staticOptions: ScalesOptions[];
  yScale: ScaleBand<string>;
  keyAccessor: (d: ScalesData | ScalesOptions) => string;
  labelAccessor: (d: ScalesOptions) => string;
  marginTop: number;
  itemHeight: number;
  color: string;
  fontFamily: string;
  renderChoiceLabel?: RenderChoiceLabel | undefined;
}

export const ScalesOptionLabels = ({
  width,
  height,
  staticOptions,
  keyAccessor,
  labelAccessor,
  marginTop,
  yScale,
  itemHeight,
  color,
  fontFamily,
  renderChoiceLabel,
}: ScalesOptionLabelsProps) => {
  const labelsContainerRef = useRef<HTMLDivElement | null>(null);

  const longestLabelId = getLongestLabel(
    staticOptions,
    labelAccessor,
    keyAccessor,
  );

  return (
    <Box
      position="absolute"
      top={marginTop}
      left={MARGIN_HORIZONTAL}
      width={width}
      height={height}
      ref={labelsContainerRef}
      extend={() => ({ opacity: 0, pointerEvents: 'none' })}
    >
      {staticOptions.map((option) => {
        const id = keyAccessor(option);
        const labelValue = labelAccessor(option);
        const isLongestLabel = longestLabelId === id;

        const itemYMargin = getAverageScoreCircleRadius(itemHeight) / 2;
        const textHeight = itemHeight - itemYMargin * 2;
        const top = (yScale(id) ?? 0) + itemYMargin;

        const label = (
          <VisualizationTextMarkupRenderer
            editorContentProps={{
              style: {
                fontFamily,
                fontSize: 'inherit',
              },
            }}
            content={labelValue}
          />
        );

        const labelOverride = renderChoiceLabel?.({
          node: label,
          data: {
            id,
            label: labelValue,
          },
        });

        return (
          <Box
            key={id}
            width="100%"
            height={textHeight}
            position="absolute"
            top={`${top}px`}
            pb="1%"
            pr={SKIP_TEXT_WIDTH}
            data-testid="option-label"
          >
            <Box
              data-testid="label-container"
              width="100%"
              height="100%"
              justifyContent="center"
              style={{
                wordBreak: 'break-word',
                color,
              }}
            >
              {isLongestLabel && (
                <TextFitGhost
                  text={labelValue}
                  fontSizes={VISUALIZATION_LABEL_FONTSIZES}
                  width={Math.max(width - SKIP_TEXT_WIDTH, 0)}
                  height={textHeight}
                  finalFontSizeContainerRef={labelsContainerRef}
                >
                  {(ref) => (
                    <Text aria-hidden="true" ref={ref}>
                      {labelValue}
                    </Text>
                  )}
                </TextFitGhost>
              )}
              {labelOverride ?? label}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
